/**
 * ProductivityBar - what the AI gave back, last 30 days.
 *
 * Restyled onto the KPI-strip standard: the headline figures are `KpiTile`s in
 * a divided `GlassCard`, and the per-surface breakdown is a column of `Well`s
 * beside them instead of a row of MUI `Tooltip`s. The tooltips were the real
 * problem - the detail they hid ("~4h saved across 12 matters") is the only
 * reason the breakdown is interesting, and it was reachable only by hovering,
 * which is to say not reachable at all on a tablet.
 *
 * IT IS HALF A ROW WIDE NOW, beside Recent Activity. Full width made it a
 * letterbox: three numbers stretched across twelve columns with a five-across
 * strip of wells underneath. So the body is TWO COLUMNS - figures on the left,
 * breakdown on the right - which is what a half-width card can actually hold,
 * and it stacks below `lg` like everything else.
 *
 * THE WINDOW IS A BADGE, not a subtitle, matching Firm Economics: the period a
 * card covers is state, and state belongs beside the title. Unlike economics
 * it is not yet a CONTROL - `/dashboard/summary` computes a fixed trailing 30
 * days and takes no date parameters, so a picker here would move nothing.
 *
 * F-TIME: the figures are the firm's. Every item counts its hours by hand and
 * with PONS - from its own override, the firm's assumptions (Settings ›
 * Organization › Time saved) or PONS's estimate - and the saving is valued at
 * the user's billing rate in the firm's currency, which the payload names. The
 * per-browser "Estimates" sliders this card used to have are gone: one
 * person's guess shouldn't change what the firm reports.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiBriefcaseLight,
  PiCalendarBlankLight,
  PiChatCircleDotsLight,
  PiClockLight, PiCheckCircleLight,
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiSlidersLight,
  PiTableLight,
  PiTrendUpLight,
} from 'react-icons/pi';
import { DashboardProductivity } from '../../../store/reducer/DashboardReducer';
import { useAppSelector, RootState } from '../../../store';
import { formatCurrency } from '../../../utils/formatCurrency';
import { settingsSectionPath } from '../../Settings/framework/settingsRegistry';
import GlassCard from '../../shared/brand/GlassCard';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { KpiTile } from '../../shared/brand/Field';
import { formatDuration } from '../../../utils/timeSavedApi';

interface ProductivityBarProps {
  productivity: DashboardProductivity | null;
}

type Surface = 'matters' | 'reviews' | 'drafts' | 'research' | 'tables';

const ProductivityBar: React.FC<ProductivityBarProps> = ({ productivity }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isAdmin = useAppSelector((st: RootState) => !!(st.user as any)?.userInfo?.is_org_admin);

  if (!productivity || (productivity.hours_saved === 0 && productivity.tasks_completed_30d === 0)) {
    return null;
  }

  const hoursSaved = Math.round(productivity.hours_saved || 0);
  const manualHours = Math.round(productivity.manual_hours ?? 0);
  // Measured processing time (R4-14) - minutes, mostly, so not rounded to hours.
  const ponsTime = formatDuration(productivity.pons_hours ?? 0, t);
  const costSaved = productivity.currency
    ? formatCurrency(Math.round(productivity.cost_saved || 0), productivity.currency).replace(/[.,]00$/, '')
    : (productivity.cost_saved || 0).toLocaleString();

  const SURFACES: Array<{ key: Surface; icon: React.ReactNode; label: string }> = [
    { key: 'matters', icon: <PiBriefcaseLight />, label: t('Matters') },
    { key: 'reviews', icon: <PiFileMagnifyingGlassLight />, label: t('Reviews') },
    { key: 'drafts', icon: <PiFileTextLight />, label: t('Drafts') },
    { key: 'research', icon: <PiChatCircleDotsLight />, label: t('Research') },
    { key: 'tables', icon: <PiTableLight />, label: t('Tables') },
  ];

  const breakdown = SURFACES
    .map((s) => ({ ...s, data: productivity.breakdown[s.key], saved: productivity.breakdown[s.key]?.hours ?? 0 }))
    .filter((item) => (item.data?.count ?? 0) > 0);

  const maxSaved = Math.max(0.0001, ...breakdown.map((b) => b.saved));

  return (
    <GlassCard
      icon={<PiTrendUpLight />}
      title={t('Productivity')}
      actions={
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <Button
              variant="outline"
              size="small"
              onClick={() => history.push(`${settingsSectionPath('org-profile')}#time-saved`)}
              icon={<PiSlidersLight className="h-3.5 w-3.5" />}
              ariaLabel={t('Change the firm’s time-saved estimates')}
            >
              {t('Estimates')}
            </Button>
          )}
          <Badge color="gray" icon={<PiCalendarBlankLight className="h-3 w-3" />}>
            {t('Last {{count}} days', { count: 30 })}
          </Badge>
        </div>
      }
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="productivity"
    >
      {/*
        Two columns from `lg` up - figures beside their explanation. One column
        below it, where the card is the full width of a phone anyway.
      */}
      {/* Gaps rather than rules, as in the other metric strips: a cross drawn
          through a card makes its inside read as a table of cells. */}
      {/*
        Same shape as Firm Economics: a compact KPI strip ON TOP, then the
        breakdown filling the space below. The old layout stood the three KPIs
        in a column that stretched (auto-rows-fr) down the full card height, so
        the figures drifted apart and sat beside, rather than above, their
        explanation — which is what read as misaligned against the Firm
        Economics card next to it. Now the two cards line up: title, strip,
        body.
      */}
      <div className="grid flex-shrink-0 grid-cols-3 gap-1 p-1">
        <KpiTile
          icon={<PiClockLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={`${hoursSaved}h`}
          label={t('Hours saved')}
          hint={t('{{manual}}h by hand → {{pons}} with PONS', { manual: manualHours, pons: ponsTime })}
        />
        <KpiTile
          icon={<PiTrendUpLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={costSaved}
          label={t('Estimated value saved')}
          hint={productivity.rate_source === 'user'
            ? t('At your billing rate')
            : t('At the firm’s rate')}
        />
        <KpiTile
          icon={<PiCheckCircleLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={productivity.tasks_completed_30d.toLocaleString()}
          label={t('Tasks completed')}
          hint={t('In the same window')}
        />
      </div>

      <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto border-t border-hairline-soft p-3">
        {breakdown.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-xs text-fg-muted">{t('No AI activity to break down yet.')}</p>
            </div>
          ) : (
            /* ROI visualisation: a proportional bar per surface, read down by
               hours saved - the same bar family the AI-usage breakdowns use. */
            breakdown.map((item) => (
              <div key={item.key} className="flex-shrink-0">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex min-w-0 items-center gap-1.5 text-fg-body">
                    <span className="flex-shrink-0 text-fg-muted [&>svg]:h-3.5 [&>svg]:w-3.5">{item.icon}</span>
                    <span className="truncate">{item.label}</span>
                    <span className="flex-shrink-0 text-[11px] text-fg-muted tabular-nums">· {item.data.count}</span>
                    {item.data.manual_hours != null && (
                      <span className="flex-shrink-0 text-[11px] text-fg-muted tabular-nums">
                        · {t('{{manual}}h → {{pons}}', {
                          manual: Math.round(item.data.manual_hours), pons: formatDuration(item.data.pons_hours ?? 0, t),
                        })}
                      </span>
                    )}
                  </span>
                  <span className="flex-shrink-0 font-medium text-success-600 tabular-nums dark:text-success-400">
                    {t('{{hours}}h', { hours: Math.round(item.saved) })}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
                  <div
                    className="h-full rounded-full bg-success-500 transition-all duration-300 ease-slide"
                    style={{ width: `${Math.max(3, (item.saved / maxSaved) * 100)}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
    </GlassCard>
  );
};

export default ProductivityBar;
